import React, {
  ElementType,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Menu,
  MenuItem,
  SxProps,
  Tab,
  TabProps,
  Tabs,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { LinkProps, useLocation } from 'react-router-dom';

import {
  MerchantAwareLink as Link,
  MerchantAwareLinkProps,
} from '../../components/core/MerchantAwareLink';
import { tabs, MainTab, configurationPaths, agentHubPaths } from '../../util/constants';
import { useMerchant } from '../../contexts/MerchantContext';
import { useCanManagePortalUsers, useIsSkipifyAdmin } from '../../hooks';
import {
  MOBILE_BAR_SOFT_CAP,
  NavItem,
  orderNavByPriority,
  resolveVisibleNavCount,
  splitNavByPriority,
} from './tabPriority';

type Props = {
  sx?: SxProps;
};

const TEST_ID_PREFIX = 'GoCart-Merchant-Portal-Tab-Bar';

const TEST_IDS: Record<MainTab, string> = {
  CONFIGURATION: `${TEST_ID_PREFIX}-configuration`,
  PAYLINKS: `${TEST_ID_PREFIX}-paylinks`,
  CARD_ON_FILE: `${TEST_ID_PREFIX}-card-on-file`,
  INSIGHTS: `${TEST_ID_PREFIX}-insights`,
  AGENT_HUB: `${TEST_ID_PREFIX}-agent-hub`,
};

const ADMIN_TEST_ID = `${TEST_ID_PREFIX}-admin`;
const MORE_TEST_ID = `${TEST_ID_PREFIX}-more`;

const TAB_LABELS: Record<NavItem, string> = {
  CONFIGURATION: 'Settings',
  INSIGHTS: 'Insights',
  AGENT_HUB: 'Agent Hub',
  PAYLINKS: 'Paylinks',
  CARD_ON_FILE: 'Card on file',
  BILLING: 'Billing',
  REWARDS: 'Rewards',
  ADMIN: 'Admin',
};

type StyledTabProps = TabProps & {
  component?: ElementType;
  to?: LinkProps['to'];
};

const tabSx = {
  textTransform: 'initial' as const,
  fontSize: { xs: 12, sm: 14 },
  color: 'text.primary',
  fontWeight: 400,
  minWidth: { xs: 'auto', sm: 'auto' },
  flex: { xs: 1, sm: 'none' },
  mb: -0.5,
  px: { xs: 0.5, sm: 1, md: 3 },
  '& .MuiSvgIcon-root': {
    marginBottom: '0px !important',
    marginLeft: '4px !important',
    fontSize: 16,
  },
  '&.Mui-selected': {
    color: 'text.primary',
    fontWeight: 600,
  },
};

const StyledTab = ({ sx, ...props }: StyledTabProps) => (
  <Tab
    disableRipple
    sx={{
      ...tabSx,
      ...sx,
    }}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
);

const isAdminPath = (path: string) =>
  path.startsWith(configurationPaths.PORTAL_USERS) || path.startsWith(configurationPaths.ADMIN);

const isFutureNavItem = (item: NavItem): item is 'BILLING' | 'REWARDS' =>
  item === 'BILLING' || item === 'REWARDS';

const isNavItemActive = (
  item: NavItem,
  path: string,
  tabPaths: Partial<Record<MainTab, string>>,
) => {
  if (item === 'ADMIN') return isAdminPath(path);
  if (isFutureNavItem(item)) return false;
  const tabPath = tabPaths[item];
  if (!tabPath) return false;

  // Match top-level product segment only (`/paylinks`, `/paylinks/...`).
  // Avoid `includes`: `/settings/paylinks` is Settings config, not the Paylinks tab.
  const segment = `/${tabPath}`;
  return path === segment || path.startsWith(`${segment}/`);
};

const getNavItemTo = (
  item: NavItem,
  adminDestination: MerchantAwareLinkProps['to'],
): MerchantAwareLinkProps['to'] | undefined => {
  if (item === 'ADMIN') return adminDestination;
  if (isFutureNavItem(item)) return undefined;
  if (item === 'AGENT_HUB') return agentHubPaths.DASHBOARD;
  return `/${tabs[item]}` as MerchantAwareLinkProps['to'];
};

const TabBar: React.FC<Props> = ({ sx }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const isHoverOpenEnabled = useMediaQuery(theme.breakpoints.up('md'));
  const isSkipifyAdmin = useIsSkipifyAdmin();
  const canManagePortalUsers = useCanManagePortalUsers();
  const showAdminTab = canManagePortalUsers;
  const adminTabDestination = isSkipifyAdmin
    ? configurationPaths.ADMIN_CREATE_MERCHANT
    : configurationPaths.PORTAL_USERS;
  const {
    hasMerchantAccess,
    hasAgentHubAccess,
    hasPaylinkAccess,
    hasCardOnFileAccess,
    hasInsightsAccess,
    isChildMerchantRestrictedShell,
  } = useMerchant();

  const hasMerchantAccessValue = hasMerchantAccess();
  const hasAgentHubAccessValue = hasAgentHubAccess();
  const hasPaylinkAccessValue = hasPaylinkAccess();
  const hasCardOnFileAccessValue = hasCardOnFileAccess();
  const hasInsightsAccessValue = hasInsightsAccess();

  const [moreAnchorEl, setMoreAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const morePaperRef = useRef<HTMLDivElement | null>(null);
  const moreMenuOpen = Boolean(moreAnchorEl);

  const containerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  /** Current More trigger copy — used when measuring reserved overflow width. */
  const moreLabelForMeasureRef = useRef('More');
  const [availableWidth, setAvailableWidth] = useState(0);
  const [itemWidths, setItemWidths] = useState<number[]>([]);
  const [moreWidth, setMoreWidth] = useState(0);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const closeMoreMenu = useCallback(() => {
    clearCloseTimer();
    setMoreAnchorEl(null);
  }, [clearCloseTimer]);

  const openMoreMenu = useCallback(
    (el: HTMLElement) => {
      clearCloseTimer();
      setMoreAnchorEl(el);
    },
    [clearCloseTimer],
  );

  // Grace period only for crossing the small gap between the tab and the paper
  const scheduleCloseMoreMenu = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setMoreAnchorEl(null);
    }, 300);
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  // hideBackdrop + pointer-events:none on Menu (needed for hover) disables Modal click-away.
  // Close on outside pointerdown; keep open for clicks on More tab or the menu paper.
  useEffect(() => {
    if (!moreMenuOpen) return undefined;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (moreAnchorEl?.contains(target)) return;
      if (morePaperRef.current?.contains(target)) return;
      closeMoreMenu();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [moreMenuOpen, moreAnchorEl, closeMoreMenu]);

  const permittedItems = useMemo(() => {
    const items: NavItem[] = [];
    if (hasMerchantAccessValue) items.push('CONFIGURATION');
    if (hasAgentHubAccessValue) items.push('AGENT_HUB');
    if (hasInsightsAccessValue) items.push('INSIGHTS');
    // Child-only users: hide Paylinks until a child merchant is in context (not parent picker / parent landing)
    if (hasPaylinkAccessValue && !isChildMerchantRestrictedShell) items.push('PAYLINKS');
    if (hasCardOnFileAccessValue) items.push('CARD_ON_FILE');
    if (showAdminTab) items.push('ADMIN');
    return items;
  }, [
    hasMerchantAccessValue,
    hasAgentHubAccessValue,
    hasInsightsAccessValue,
    hasPaylinkAccessValue,
    hasCardOnFileAccessValue,
    isChildMerchantRestrictedShell,
    showAdminTab,
  ]);

  const orderedItems = useMemo(() => orderNavByPriority(permittedItems), [permittedItems]);

  const tabGapPx = isMdUp ? 16 : isSmUp ? 8 : 0;

  const measure = useCallback(() => {
    // Pack against the flex shell width — not Tabs.clientWidth. MUI Tabs can shrink-wrap to
    // the already-visible bar (e.g. Settings + More), which under-reports space and leaves a
    // large empty gap before the account icon even when Agent Hub would fit.
    // Header spacing (ml) is applied on this container, not on Tabs.
    const container = containerRef.current;
    const measureRoot = measureRef.current;
    if (!container || !measureRoot) return;

    setAvailableWidth(container.clientWidth);

    const tabEls = measureRoot.querySelectorAll<HTMLElement>('[data-measure-tab]');
    setItemWidths(Array.from(tabEls).map((el) => el.getBoundingClientRect().width));

    // Reserve space for the *current* More trigger only. Using max(all promoted labels)
    // left a large empty gap after a short "More ▾".
    const moreEls = Array.from(measureRoot.querySelectorAll<HTMLElement>('[data-measure-more]'));
    const widthFor = (label: string) =>
      moreEls.find((el) => el.dataset.moreLabel === label)?.getBoundingClientRect().width ?? 0;
    const measuredMore = widthFor(moreLabelForMeasureRef.current) || widthFor('More') || 72;
    setMoreWidth(measuredMore);
  }, []);

  useLayoutEffect(() => {
    measure();
    const container = containerRef.current;
    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      measure();
    });
    if (container) observer.observe(container);
    return () => observer.disconnect();
  }, [measure, orderedItems]);

  const visibleCount = resolveVisibleNavCount({
    itemWidths,
    moreWidth,
    availableWidth,
    gap: tabGapPx,
    // Below sm only: keep bar ≤4; sm+ (incl. iPad Mini) stay fit-by-width with no count cap.
    maxVisible: isSmUp ? undefined : MOBILE_BAR_SOFT_CAP,
  });

  const { bar, more } = useMemo(
    () => splitNavByPriority(permittedItems, visibleCount),
    [permittedItems, visibleCount],
  );

  const tabPaths = tabs as Partial<Record<MainTab, string>>;
  const activeOverflowItem =
    more.find((item) => isNavItemActive(item, location.pathname, tabPaths)) ?? null;

  const selectedTabIndex = useMemo(() => {
    if (activeOverflowItem && more.length > 0) return bar.length;
    const barIndex = bar.findIndex((item) => isNavItemActive(item, location.pathname, tabPaths));
    return barIndex >= 0 ? barIndex : 0;
  }, [activeOverflowItem, bar, more.length, location.pathname, tabPaths]);

  const moreLabelBase = activeOverflowItem ? TAB_LABELS[activeOverflowItem] : 'More';
  moreLabelForMeasureRef.current = moreLabelBase;

  // Remeasure when More label promotes (wider trigger may bump one more item into overflow)
  useLayoutEffect(() => {
    measure();
  }, [measure, moreLabelBase]);

  const moreLabelNode = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      {moreLabelBase}
      <KeyboardArrowDown sx={{ fontSize: '18px !important', marginLeft: '0 !important' }} />
    </Box>
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flex: 1,
        minWidth: 0,
        width: '100%',
        // Fill HeaderBar height so the indicator sits on the bottom border
        // (parent uses alignItems: center; without stretch, taller Viewing field
        // centers the tabs and leaves a gap under the black bar).
        alignSelf: 'stretch',
        position: 'relative',
        // Header passes spacing here (e.g. ml) so it doesn't skew Tabs width math.
        ...sx,
      }}
    >
      {/*
        Clip host for the measure row. Absolute + visibility:hidden alone still expands
        scrollable overflow of unclipped ancestors (horizontal page scrollbar). Children keep
        intrinsic widths for getBoundingClientRect.
      */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <Box
          ref={measureRef}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: `${tabGapPx}px`,
            whiteSpace: 'nowrap',
            visibility: 'hidden',
          }}
        >
          {orderedItems.map((item) => (
            <Box
              key={`measure-${item}`}
              data-measure-tab
              sx={{
                ...tabSx,
                flex: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                fontFamily: theme.typography.fontFamily,
                fontWeight: 400,
              }}
            >
              {TAB_LABELS[item]}
            </Box>
          ))}
          {/* Candidates for More trigger width; fit uses current label via data-more-label */}
          {(['More', ...orderedItems.map((item) => TAB_LABELS[item])] as string[]).map((label) => (
            <Box
              key={`measure-more-${label}`}
              data-measure-more
              data-more-label={label}
              sx={{
                ...tabSx,
                flex: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                fontFamily: theme.typography.fontFamily,
              }}
            >
              {label}
              <KeyboardArrowDown sx={{ fontSize: 18 }} />
            </Box>
          ))}
        </Box>
      </Box>

      <Tabs
        ref={tabsRef}
        value={selectedTabIndex}
        sx={{
          display: 'flex',
          flex: 1,
          width: '100%',
          height: '100%',
          minHeight: '100%',
          minWidth: 0,
          '& .MuiTabs-indicator': {
            borderBottom: `4px solid`,
            borderColor: 'primary.dark',
            background: 'none',
          },
          '& .MuiTabs-flexContainer': {
            gap: { xs: 0, sm: 1, md: 2 },
            alignItems: 'stretch',
            height: '100%',
          },
        }}
      >
        {bar.map((item) => {
          // Reserved priority slots until those tabs ship
          if (isFutureNavItem(item)) return null;

          if (item === 'ADMIN') {
            return (
              <StyledTab
                data-testid={ADMIN_TEST_ID}
                label={TAB_LABELS.ADMIN}
                component={Link}
                to={adminTabDestination}
                key={`tab-${item}`}
              />
            );
          }

          return (
            <StyledTab
              data-testid={TEST_IDS[item]}
              label={TAB_LABELS[item]}
              component={Link}
              to={getNavItemTo(item, adminTabDestination)}
              key={`tab-${item}`}
            />
          );
        })}
        {more.length > 0 && (
          <StyledTab
            data-testid={MORE_TEST_ID}
            label={moreLabelNode}
            // Match other nav tabs (Links use pointer); More is a button-like tab
            sx={{ cursor: 'pointer' }}
            onClick={(event) => {
              event.stopPropagation();
              // Always open — do not toggle-close. Hover often already opened the menu;
              // a click should not dismiss it. Close via leave, outside, Escape, or item.
              openMoreMenu(event.currentTarget);
            }}
            onMouseEnter={
              isHoverOpenEnabled
                ? (event) => {
                    openMoreMenu(event.currentTarget);
                  }
                : undefined
            }
            onMouseLeave={isHoverOpenEnabled ? scheduleCloseMoreMenu : undefined}
            aria-haspopup="menu"
            aria-expanded={moreMenuOpen ? 'true' : 'false'}
          />
        )}
      </Tabs>
      {more.length > 0 && (
        <Menu
          anchorEl={moreAnchorEl}
          open={moreMenuOpen}
          onClose={closeMoreMenu}
          // Critical: default Modal backdrop sits over the More tab → mouseLeave, arrow
          // cursor flicker, and a delayed auto-close. No backdrop; paper stays interactive.
          hideBackdrop
          disableScrollLock
          disableAutoFocusItem
          MenuListProps={{
            autoFocus: false,
            onMouseEnter: isHoverOpenEnabled ? clearCloseTimer : undefined,
            onMouseLeave: isHoverOpenEnabled ? scheduleCloseMoreMenu : undefined,
            'aria-label': 'More navigation',
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          // Modal root is full-viewport even with hideBackdrop. Without this, it sits
          // over the More tab → mouseLeave, arrow↔pointer flicker, close after ~300ms.
          sx={{ pointerEvents: 'none' }}
          slotProps={{
            paper: {
              ref: morePaperRef,
              sx: {
                pointerEvents: 'auto',
                minWidth: 180,
                cursor: 'default',
                mt: 0.5,
              },
              onMouseEnter: isHoverOpenEnabled ? clearCloseTimer : undefined,
              onMouseLeave: isHoverOpenEnabled ? scheduleCloseMoreMenu : undefined,
            },
          }}
        >
          {more.map((item) => {
            // Reserved priority slots until those tabs ship
            if (isFutureNavItem(item)) return null;

            const label = TAB_LABELS[item];
            const testId = item === 'ADMIN' ? ADMIN_TEST_ID : TEST_IDS[item];
            const isActive = activeOverflowItem === item;
            const to = getNavItemTo(item, adminTabDestination)!;
            return (
              <MenuItem
                key={item}
                data-testid={testId}
                component={Link}
                to={to}
                selected={isActive}
                onClick={() => closeMoreMenu()}
                sx={{ fontWeight: isActive ? 600 : 400, fontSize: 14, cursor: 'pointer' }}
              >
                {label}
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </Box>
  );
};

export const test = {
  UnconnectedTabBar: TabBar,
  TEST_IDS,
  MORE_TEST_ID,
  ADMIN_TEST_ID,
  isNavItemActive,
};

export default TabBar;
